
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChartBar, FaStoreAltSlash } from 'react-icons/fa';
import { LuMapPin, LuMapPinOff } from 'react-icons/lu';
import { useTheme } from 'styled-components';

import { MdRemoveShoppingCart, MdShoppingCart } from 'react-icons/md';
import { TbShoppingCartOff, TbCalendarOff } from 'react-icons/tb';
import { BiTargetLock } from 'react-icons/bi';
import AsyncTable, { Column, RequestPage } from '../../../../../../components/AsyncTable';
import { backendErrorNotification } from '../../../../../../components/Notification';
import { ActionButtonsContainer } from '../../../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../../../enums/httpStatus';
import useDashboardFilters from '../../../../../../hooks/useDashboardFilters';
import { PreviewIcon } from '../../../../../../icons';
import { visitMetricsApi, visitScheduledAPI } from '../../../../../../services/requests';
import { ScheduledVisit } from '../../../../../../types';
import { ApiResPaginated } from '../../../../../../types/apiResponseTypes';
import { getPercent } from '../../../../../../utils/helpers';
import ModalVisitScheduleDetail
  from '../../../../VisitSchedule/VisitSchedule/Components/ModalVisitScheduleDetail';
import CardIcon from './Components/CardIcon';
import GraphLineSchedules from './Components/GraphLineSchedules';
import Loader from './Components/Loader';
import * as s from './styles';
import { VisitMetrics } from './types';

const Main = () => {
  const { t } = useTranslation();

  const theme = useTheme();

  const {
    filters: { filters },
  } = useDashboardFilters();

  const initialData: VisitMetrics = {
    graphData: [],
    metrics: {
      salesVisits: 0,
      noSalesVisits: 0,
      missedVisits: 0,
      totalVisits: 0,
      checkinsWithinRadius: 0,
      checkinsOutsideRadius: 0,
      checkoutsOutsideRadius: 0,
      salesOutsideRadius: 0,
      visitsWithoutHistory: 0,
    },
  };

  const [metrics, setMetrics] = useState<VisitMetrics>(initialData);
  const [scheduledVisits, setScheduledVisits] = useState<ScheduledVisit[]>([]);
  const [visitsFilterCategory, setVisitsFilterCategory] = useState<string[]>(['allVisits']);
  const [selectedVisit, setSelectedVisit] = useState<ScheduledVisit | null>(null);
  const [showVisitModal, setShowVisitModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const handleViewVisit = (visit: ScheduledVisit) => {
    setSelectedVisit(visit);
    setShowVisitModal(true);
  };

  const columns: Column<ScheduledVisit>[] = [
    {
      label: t('sellerCode') as string,
      accessor: 'CodigoVendedor',
      Cell: (row) => `${row.CodigoVendedor}${row.seller ? ` - ${row.seller.Nome}` : ''}`,
    },
    {
      label: t('customerCode') as string,
      accessor: 'CodigoCliente',
      Cell: (row) => `${row.CodigoCliente}${row.customer ? ` - ${row.customer.RazaoSocial}` : ''}`,
    },
    {
      label: t('scheduledDate') as string,
      accessor: 'scheduled_for',
    },
    {
      label: t('origin') as string,
      accessor: 'origin',
      Cell: (row) => t(row.origin) as string,
    },
    {
      label: t('note') as string,
      accessor: 'obs',
    },
    {
      label: t('actions') as string,
      accessor: 'id',
      Cell: (row) => (
        <ActionButtonsContainer>
          <PreviewIcon title={t('view')} onClick={() => handleViewVisit(row)} />
        </ActionButtonsContainer>
      ),
    },
  ];

  const requestDataForAsyncTable: RequestPage = async ({ search, page, quantityPerPage }) => {
    let hasMore = true;
    const source = visitScheduledAPI.axios.CancelToken.source();

    try {
      let query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;

      if (visitsFilterCategory.length > 0 && !visitsFilterCategory.includes('allVisits')) {
        if (visitsFilterCategory.length === 1) {
          query += `&history_type=${visitsFilterCategory[0]}`;
        }
      }

      if (filters.startPeriod) {
        query += `&scheduled_for_start=${filters.startPeriod}`;
      }
      if (filters.endPeriod) {
        query += `&scheduled_for_end=${filters.endPeriod}`;
      }

      const filterMap = {
        sellers: 'CodigoVendedor',
        customers: 'CodigoCliente',
      } as const;

      Object.entries(filterMap).forEach(([filterName, queryName]) => {
        const f = filters as any;
        if (f[filterName] && f[filterName].length > 0) {
          query += `&filter[${queryName}]=${f[filterName].join(',')}`;
        }
      });

      visitScheduledAPI.query = query;

      const res = await visitScheduledAPI.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<ScheduledVisit[]> = res.data;

      if (data.length === 0) {
        hasMore = false;
      }

      return {
        data,
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!visitScheduledAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const getMetrics = async () => {
    setIsLoading(true);

    const source = visitMetricsApi.axios.CancelToken.source();

    let query = '?q=q'; //q=q apenas para iniciar a query, nao filtra nada

    if (filters.startPeriod) {
      query += `&scheduled_for_start=${filters.startPeriod}`;
    }
    if (filters.endPeriod) {
      query += `&scheduled_for_end=${filters.endPeriod}`;
    }

    const filterMap = {
      sellers: 'CodigoVendedor',
      customers: 'CodigoCliente',
    } as const;

    Object.entries(filterMap).forEach(([filterName, queryName]) => {
      const f = filters as any;
      if (f[filterName] && f[filterName].length > 0) {
        query += `&filter[${queryName}]=${f[filterName].join(',')}`;
      }
    });

    visitMetricsApi.query = query;

    try {
      const res = await visitMetricsApi.index(source.token);

      if (res.status === HttpStatus.OK) {
        const { data }: { data: VisitMetrics; } = res.data;
        setMetrics(data);
      } else {
        throw res;
      }
    } catch (err) {
      if (!visitMetricsApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
    return () => {
      source.cancel('Component got unmounted');
    };
  };

  useEffect(() => {
    getMetrics();
  }, [filters]);

  /**
     * Essa funcao ficou complexa entao vale o comentario:

     --- Se o filtro selecionado for "allVisits" e já estiver presente, nada
     mais é feito. Pois nao pode ficar sem filtro.

     --- Se "allVisits" for selecionado e não estiver presente, ele é adicionado
     e todos os outros filtros são removidos. Pois nao faz sentido ter "allVisits"
     e outro filtro (all ja é tudo).

     --- Se outro filtro for selecionado e "allVisits" estiver presente,
     "allVisits" é removido e o novo filtro é adicionado. Pois nesse caso se
     nao remover allVisits, nunca vai filtrar so pelo novo filtro, sempre vai
     trazer tudo.

     --- Se um filtro for removido e isso deixar o array vazio, "allVisits"
     é adicionado automaticamente. Pois tem que ter pelo menos 1 filtro.

     --- Se um filtro que não é "allVisits" for selecionado e ele
     já estiver no array, ele é removido e verifica-se se o array ficou
     vazio para possivelmente adicionar "allVisits".
     */
  const handleFilterCategory = (categoryName: string) => {
    const exclusiveFilters = ['allVisits', 'noHistory'];

    setVisitsFilterCategory((prevCategories) => {
      const isExclusive = exclusiveFilters.includes(categoryName);
      const hasExclusiveFilter = prevCategories.some((c) => exclusiveFilters.includes(c));

      if (isExclusive) {
        if (prevCategories.includes(categoryName)) {
          return prevCategories;
        }
        return [categoryName];
      }

      if (hasExclusiveFilter) {
        return [categoryName];
      }

      const index = prevCategories.indexOf(categoryName);
      if (index !== -1) {
        const newCategories = prevCategories.filter((c) => c !== categoryName);
        if (newCategories.length === 0) {
          return ['allVisits'];
        }
        return newCategories;
      }

      return [...prevCategories, categoryName];
    });
  };

  const checkIfFilterIsSet = (categoryName: string): boolean => {
    if (categoryName === 'noHistory') {
      return visitsFilterCategory.includes('noHistory');
    }

    if (visitsFilterCategory.includes('noHistory')) {
      return false;
    }

    return (
      visitsFilterCategory.includes(categoryName) || visitsFilterCategory.includes('allVisits')
    );
  };

  const handlePercents = (type: string) => {
    switch (type) {
      case 'salesVisits':
        return getPercent(metrics.metrics.totalVisits, metrics.metrics.salesVisits);
      case 'noSalesVisits':
        return getPercent(metrics.metrics.totalVisits, metrics.metrics.noSalesVisits);
      case 'missedVisits':
        return getPercent(metrics.metrics.totalVisits, metrics.metrics.missedVisits);
      case 'checkinsWithinRadius':
        return getPercent(metrics.metrics.totalVisits, metrics.metrics.checkinsWithinRadius);
      case 'checkinsOutsideRadius':
        return getPercent(metrics.metrics.totalVisits, metrics.metrics.checkinsOutsideRadius);
      case 'checkoutsOutsideRadius':
        return getPercent(metrics.metrics.totalVisits, metrics.metrics.checkoutsOutsideRadius);
      case 'salesOutsideRadius':
        return getPercent(metrics.metrics.totalVisits, metrics.metrics.salesOutsideRadius);
      case 'visitsWithoutHistory':
        return getPercent(metrics.metrics.totalVisits, metrics.metrics.visitsWithoutHistory);
      default:
        return 0;
    }
  };

  return (
    <Loader isLoading={isLoading}>
      <s.Pag1Container>
        {metrics.graphData.length > 0 && (
          <s.GraphsContainer>
            <GraphLineSchedules data={metrics.graphData} />
          </s.GraphsContainer>
        )}

        <s.InfoCards>
          <CardIcon
            color="#6c63ff"
            icon={<FaChartBar />}
            title={t('totalVisits')}
            text={metrics.metrics.totalVisits as unknown as string}
            onClick={() => handleFilterCategory('allVisits')}
            disabled={!checkIfFilterIsSet('allVisits')}
          />

          <CardIcon
            color="#1D98DF"
            icon={<MdShoppingCart />}
            title={t('salesVisits')}
            text={`${metrics.metrics.salesVisits} | ${handlePercents('salesVisits')}%`}
            onClick={() => handleFilterCategory('ve')}
            disabled={!checkIfFilterIsSet('ve')}
          />

          <CardIcon
            color="#cfa705"
            icon={<MdRemoveShoppingCart />}
            title={t('noSalesVisits')}
            text={`
                            ${metrics.metrics.noSalesVisits} | ${handlePercents('noSalesVisits')}%
                        `}
            onClick={() => handleFilterCategory('sve')}
            disabled={!checkIfFilterIsSet('sve')}
          />

          <CardIcon
            color="#CD1719"
            icon={<FaStoreAltSlash />}
            title={t('missedVisits')}
            text={`
                            ${metrics.metrics.missedVisits} | ${handlePercents('missedVisits')}%
                        `}
            onClick={() => handleFilterCategory('svi')}
            disabled={!checkIfFilterIsSet('svi')}
          />

          <CardIcon
            color="#19870f"
            icon={<LuMapPin />}
            title={t('checkinsWithinRadius')}
            text={`
                            ${metrics.metrics.checkinsWithinRadius} |
                            ${handlePercents('checkinsWithinRadius')}
                        %`}
            onClick={() => handleFilterCategory('checkinsWithinRadius')}
            disabled={!checkIfFilterIsSet('checkinsWithinRadius')}
          />

          <CardIcon
            color="#DE7223"
            icon={<LuMapPinOff />}
            title={t('checkinsOutsideRadius')}
            text={`
                            ${metrics.metrics.checkinsOutsideRadius} |
                            ${handlePercents('checkinsOutsideRadius')}%
                        `}
            onClick={() => handleFilterCategory('checkinsOutsideRadius')}
            disabled={!checkIfFilterIsSet('checkinsOutsideRadius')}
          />

          <CardIcon
            color="#8B4513"
            icon={<BiTargetLock />}
            title={t('checkoutsOutsideRadius')}
            text={`
                            ${metrics.metrics.checkoutsOutsideRadius} |
                            ${handlePercents('checkoutsOutsideRadius')}%
                        `}
            onClick={() => handleFilterCategory('checkoutsOutsideRadius')}
            disabled={!checkIfFilterIsSet('checkoutsOutsideRadius')}
          />

          <CardIcon
            color="#9B59B6"
            icon={<TbShoppingCartOff />}
            title={t('salesOutsideRadius')}
            text={`
                            ${metrics.metrics.salesOutsideRadius} |
                            ${handlePercents('salesOutsideRadius')}%
                        `}
            onClick={() => handleFilterCategory('vfr')}
            disabled={!checkIfFilterIsSet('vfr')}
          />

          <CardIcon
            color="#34495e"
            icon={<TbCalendarOff />}
            title={t('visitsWithoutHistory')}
            text={`
                            ${metrics.metrics.visitsWithoutHistory} |
                            ${handlePercents('visitsWithoutHistory')}%
                        `}
            onClick={() => handleFilterCategory('noHistory')}
            disabled={!checkIfFilterIsSet('noHistory')}
          />
        </s.InfoCards>

        <s.TablesArea>
          <div className="top-visits-performers-table">
            <s.TableLabel>{t('scheduledVisits')}</s.TableLabel>

            <AsyncTable
              tableName={t('scheduledVisits')}
              columns={columns}
              value={scheduledVisits}
              onChange={setScheduledVisits}
              requestPage={requestDataForAsyncTable}
              reqListener={[filters, visitsFilterCategory]}
              options={{
                styles: {
                  primaryColor: `${theme.colors.surface}`,
                  secondaryColor: `${theme.colors.onSurface}`,
                  alternateRowColor: theme.colors.onSurfaceLight,
                  textColor: theme.colors.text,
                },
                quantityPerPageLabel: t('quantityPerPage'),
                searchPlaceholder: t('search'),
              }}
            />
          </div>
        </s.TablesArea>

        {showVisitModal && selectedVisit && (
          <ModalVisitScheduleDetail
            seller={
              selectedVisit.seller
                ? { value: selectedVisit.seller.CodigoVendedor, label: selectedVisit.seller.Nome }
                : {}
            }
            visit={selectedVisit}
            initialDate={new Date(selectedVisit.scheduled_for.replace(' ', 'T'))}
            closeModal={() => {
              setShowVisitModal(false);
              setSelectedVisit(null);
            }}
            onSuccess={async () => {
              setShowVisitModal(false);
              setSelectedVisit(null);
            }}
          />
        )}
      </s.Pag1Container>
    </Loader>
  );
};

export default Main;
